import loginAPI from "apis/LoginAPI";
import LoadingScreen from "components/LoadingScreen";
import { appInfoLoader } from "components/authentication/AppInfoLoader";
import { ReactNode, createContext, useEffect, useReducer } from "react";
import axios from "utils/axios";
import { isValidToken, setSession } from "utils/sessionUtil";

// All types
// =============================================
type Primitive = string | number | boolean | null;
type JsonValue = Primitive | JsonValue[] | { [key: string]: JsonValue };

export type ActionMap<M extends Record<string, object | undefined>> = {
  [Key in keyof M]: M[Key] extends undefined
    ? {
        type: Key;
      }
    : {
        type: Key;
        payload: M[Key];
      };
};

export type AuthUser = null | Record<string, JsonValue>;

export type AuthState = {
  isAuthenticated: boolean;
  isInitialized: boolean;
  user: AuthUser;
};

enum Types {
  Init = "INIT",
  Login = "LOGIN",
  CustomerLogin = "CUSTOMER_LOGIN",
  Logout = "LOGOUT",
  Register = "REGISTER",
}

type JWTAuthPayload = {
  [Types.Init]: {
    isAuthenticated: boolean;
    user: AuthUser;
  };
  [Types.Logout]: undefined;
  [Types.Login]: { user: AuthUser };
  [Types.CustomerLogin]: { user: AuthUser };
  [Types.Register]: { user: AuthUser };
};

type JWTActions = ActionMap<JWTAuthPayload>[keyof ActionMap<JWTAuthPayload>];
// ================================================

type LoginApiResponse = {
  token: string;
  user: AuthUser;
};

type RegisterApiResponse = {
  accessToken: string;
  user: AuthUser;
};

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

// const isValidToken = (accessToken: string) => {
//   if (!accessToken) return false;

//   const decodedToken = jwtDecode<{ exp: number }>(accessToken);
//   const currentTime = Date.now() / 1000;
//   return decodedToken.exp > currentTime;
// };

// const setSession = (accessToken: string | null, userData: Object | null = null) => {
//   if (accessToken) {
//     localStorage.setItem("accessToken", accessToken);
//     if (userData) localStorage.setItem("userData", JSON.stringify(userData));
//     axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
//   } else {
//     localStorage.removeItem("accessToken");
//     localStorage.removeItem("userData");
//     delete axios.defaults.headers.common.Authorization;
//   }
// };

const reducer = (state: AuthState, action: JWTActions) => {
  switch (action.type) {
    case "INIT": {
      return {
        isInitialized: true,
        user: action.payload.user,
        isAuthenticated: action.payload.isAuthenticated,
      };
    }
    case "LOGIN": {
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
      };
    }
    case "CUSTOMER_LOGIN": {
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
      };
    }
    case "LOGOUT": {
      return {
        ...state,
        user: null,
        isAuthenticated: false,
      };
    }
    case "REGISTER": {
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
      };
    }

    default: {
      return state;
    }
  }
};

const AuthContext = createContext({
  ...initialState,
  method: "JWT",
  login: (email: string, password: string) => Promise.resolve(),
  customerLogin: (email: string, password: string) => Promise.resolve(),
  logout: () => {},
  register: (
    email: string,
    password: string,
    username: string,
    tradeName: string,
    cnpj: string
  ) => Promise.resolve(),
});

// props type
type AuthProviderProps = {
  children: ReactNode;
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const login = async (email: string, password: string) => {
    const response = await loginAPI.login(email, password);
    const { token, user } = response.data as LoginApiResponse;

    setSession(token, user);
    dispatch({
      type: Types.Login,
      payload: {
        user,
      },
    });
  };

  const customerLogin = async (email: string, password: string) => {
    const response = await loginAPI.customerLogin(email, password);
    const { token, user } = response.data as LoginApiResponse;

    setSession(token, user);
    dispatch({
      type: Types.Login,
      payload: {
        user,
      },
    });
  };

  const register = async (
    email: string,
    username: string,
    password: string
  ) => {
    const response = await axios.post("/api/auth/register", {
      email,
      username,
      password,
    });
    const { accessToken, user } = response.data as RegisterApiResponse;
    setSession(accessToken, user);
    dispatch({
      type: Types.Register,
      payload: {
        user,
      },
    });
  };

  const logout = () => {
    setSession(null);
    dispatch({ type: Types.Logout });
  };

  useEffect(() => {
    (async () => {
      try {
        const accessToken = window.localStorage.getItem("accessToken");

        if (accessToken && isValidToken(accessToken)) {
          setSession(accessToken);

          const user = window.localStorage.getItem("userData") || "";
          appInfoLoader();
          dispatch({
            type: Types.Init,
            payload: {
              user: JSON.parse(user) || {},
              isAuthenticated: true,
            },
          });
        } else {
          dispatch({
            type: Types.Init,
            payload: {
              user: null,
              isAuthenticated: false,
            },
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: Types.Init,
          payload: {
            user: null,
            isAuthenticated: false,
          },
        });
      }
    })();
  }, []);

  if (!state.isInitialized) {
    return <LoadingScreen />;
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: "JWT",
        login,
        customerLogin,
        logout,
        register,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
